import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import AIChatbot from "../components/AIChatbot";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import {
  BookOpen,
  Award,
  Clock,
  Play,
  TrendingUp,
  Crown,
  Loader2,
  ArrowRight,
  Terminal,
} from "lucide-react";

const Dashboard = () => {
  const { user, token } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/student`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDashboardData(response.data);
    } catch (error) {
      console.error("Error fetching dashboard:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const { enrollments = [], courses = [], certificates = [], subscription, total_courses, completed_courses } = dashboardData || {};

  return (
    <div className="min-h-screen bg-background" data-testid="student-dashboard">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-heading font-bold mb-2">
            Bonjour, {user?.name?.split(" ")[0]} 👋
          </h1>
          <p className="text-muted-foreground">
            Continuez votre apprentissage et atteignez vos objectifs DevOps.
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary/20 rounded-lg flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-heading font-bold">{total_courses}</p>
                <p className="text-sm text-muted-foreground">Cours inscrits</p>
              </div>
            </div>
          </div>

          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-secondary/20 rounded-lg flex items-center justify-center">
                <Award className="w-5 h-5 text-secondary" />
              </div>
              <div>
                <p className="text-2xl font-heading font-bold">{completed_courses}</p>
                <p className="text-sm text-muted-foreground">Cours terminés</p>
              </div>
            </div>
          </div>

          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-accent/20 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-accent" />
              </div>
              <div>
                <p className="text-2xl font-heading font-bold">{certificates.length}</p>
                <p className="text-sm text-muted-foreground">Certificats</p>
              </div>
            </div>
          </div>

          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                <Crown className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="text-2xl font-heading font-bold">
                  {subscription ? "Pro" : "Free"}
                </p>
                <p className="text-sm text-muted-foreground">Abonnement</p>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Courses In Progress */}
          <div className="lg:col-span-2">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-heading font-bold">Mes cours</h2>
              <Link to="/courses">
                <Button variant="ghost" size="sm">
                  Voir tout
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </div>

            {enrollments.length === 0 ? (
              <div className="bg-card border border-border rounded-lg p-8 text-center">
                <BookOpen className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-heading font-bold text-lg mb-2">
                  Aucun cours pour le moment
                </h3>
                <p className="text-muted-foreground mb-4">
                  Explorez notre catalogue et commencez votre apprentissage DevOps.
                </p>
                <Link to="/courses">
                  <Button className="glow-green">
                    Explorer les cours
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-4" data-testid="enrolled-courses">
                {enrollments.map((enrollment) => {
                  const course = courses.find(c => c.course_id === enrollment.course_id);
                  if (!course) return null;

                  return (
                    <Link 
                      key={enrollment.enrollment_id} 
                      to={`/courses/${course.course_id}`}
                      className="block"
                    >
                      <div className="bg-card border border-border rounded-lg p-4 hover:border-primary/50 transition-colors">
                        <div className="flex gap-4">
                          <img
                            src={course.thumbnail || "https://images.unsplash.com/photo-1667372393119-3d4c48d07fc9?w=200"}
                            alt={course.title}
                            className="w-24 h-16 object-cover rounded"
                          />
                          <div className="flex-1 min-w-0">
                            <h3 className="font-heading font-bold truncate mb-1">
                              {course.title}
                            </h3>
                            <div className="flex items-center gap-4 text-sm text-muted-foreground mb-2">
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {course.duration_hours}h
                              </span>
                            </div>
                            <div className="flex items-center gap-3">
                              <Progress 
                                value={enrollment.progress_percent} 
                                className="flex-1 h-2"
                              />
                              <span className="text-sm font-medium text-primary">
                                {Math.round(enrollment.progress_percent)}%
                              </span>
                            </div>
                          </div>
                          <Button size="icon" variant="ghost">
                            <Play className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            {/* Labs Interactifs */}
            <div className="bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border border-cyan-500/30 rounded-lg p-6">
              <Terminal className="w-8 h-8 text-cyan-400 mb-3" />
              <h3 className="font-heading font-bold text-lg mb-2">
                Labs Interactifs
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                Pratiquez Docker, Kubernetes, Terraform avec des exercices guidés.
              </p>
              <Link to="/labs">
                <Button className="w-full" variant="outline">
                  <Terminal className="w-4 h-4 mr-2" />
                  Accéder aux Labs
                </Button>
              </Link>
            </div>

            {/* Subscription Status */}
            {!subscription && (
              <div className="bg-gradient-to-br from-primary/20 to-secondary/20 border border-primary/30 rounded-lg p-6">
                <Crown className="w-8 h-8 text-primary mb-3" />
                <h3 className="font-heading font-bold text-lg mb-2">
                  Passez à Pro
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Accédez à tous les cours, labs et certifications.
                </p>
                <Link to="/pricing">
                  <Button className="w-full glow-green">
                    Voir les offres
                  </Button>
                </Link>
              </div>
            )}

            {/* Recent Certificates */}
            <div className="bg-card border border-border rounded-lg p-4">
              <h3 className="font-heading font-bold mb-4 flex items-center gap-2">
                <Award className="w-5 h-5 text-accent" />
                Certificats récents
              </h3>
              
              {certificates.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Terminez un cours pour obtenir votre premier certificat.
                </p>
              ) : (
                <div className="space-y-3">
                  {certificates.slice(0, 3).map((cert) => (
                    <div key={cert.certificate_id} className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-accent/20 rounded flex items-center justify-center">
                        <Award className="w-5 h-5 text-accent" />
                      </div>
                      <div>
                        <p className="text-sm font-medium truncate">
                          {cert.course_title}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(cert.issued_at).toLocaleDateString("fr-FR")}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {certificates.length > 0 && (
                <Link to="/certificates" className="block mt-4">
                  <Button variant="outline" size="sm" className="w-full">
                    Voir tous les certificats
                  </Button>
                </Link>
              )}
            </div>

            {/* Quick Actions */}
            <div className="bg-card border border-border rounded-lg p-4">
              <h3 className="font-heading font-bold mb-4">Actions rapides</h3>
              <div className="space-y-2">
                <Link to="/courses" className="block">
                  <Button variant="ghost" className="w-full justify-start">
                    <BookOpen className="w-4 h-4 mr-2" />
                    Parcourir les cours
                  </Button>
                </Link>
                <Link to="/profile" className="block">
                  <Button variant="ghost" className="w-full justify-start">
                    <Clock className="w-4 h-4 mr-2" />
                    Paramètres du profil
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
      <AIChatbot />
    </div>
  );
};

export default Dashboard;
